// typescript/lib/seavien-commons/src/clipboard/ClipboardHtmlDescriptor.ts
//
// Removes the Windows CF_HTML descriptor from a clipboard payload before it becomes document content.
//
// Windows prepends a header to the `HTML Format` clipboard flavor:
//
//     Version:1.0
//     StartHTML:0000000156
//     EndHTML:0000042434
//     StartFragment:0000000469
//     EndFragment:0000042426
//     SourceURL:https://example.com/post
//     <html>...
//
// The browser is supposed to parse that, strip it, and expose only the fragment through
// `clipboardData.getData("text/html")`. When the producing application writes a slightly malformed
// descriptor the strip fails and the page receives the WHOLE blob — at which point `DOMParser` hoists
// everything before `<html>` into `<body>` as a text node, and it becomes the first paragraph of the
// member's post. That is how 21 posts by one member reached prod with clipboard plumbing above their
// first line (2026-08-16). See
// designs/01-now/2026-08-16-clipboard-cf-html-descriptor-leak-design.md.
//
// Pure string helpers, no DOM and no @tiptap import, so this lives in the tree-shakeable main barrel.

/**
 * Offset keys of the descriptor. `SourceURL` is deliberately NOT in this list: it is optional, its
 * value is a URL rather than a number, and it must never be the ONLY thing that makes a block look
 * like a descriptor.
 */
const OFFSET_KEYS = [
    "StartHTML",
    "EndHTML",
    "StartFragment",
    "EndFragment",
    "StartSelection",
    "EndSelection",
] as const;

// One descriptor entry: an offset key with a numeric value, or the optional SourceURL with a
// non-whitespace value.
const ENTRY = `(?:(?:${OFFSET_KEYS.join("|")})[ \\t]*:[ \\t]*-?\\d+|SourceURL[ \\t]*:[ \\t]*\\S+)`;

/**
 * The descriptor block, anchored to the start of the string (after an optional BOM and whitespace).
 *
 * Anchored and bounded on purpose. An unanchored search would let a post that quotes a descriptor
 * mid-sentence lose text, and the real-world shape is always leading — of the payload for a paste, or
 * of its own text node for content that already carries a baked-in descriptor.
 *
 * Newlines are matched as ordinary whitespace because the descriptor arrives BOTH ways: with its
 * original line breaks at the paste boundary, and space-collapsed once it has been through an HTML
 * parser (the shape all 23 affected prod rows are stored in).
 */
const DESCRIPTOR_RE = new RegExp(
    `^[\\uFEFF\\s]*Version[ \\t]*:[ \\t]*\\d+(?:\\.\\d+)?(?:\\s*${ENTRY})+\\s*`,
);

/** Matches at least one genuine numeric offset key, so `Version:` + `SourceURL:` alone is not enough. */
const OFFSET_KEY_RE = new RegExp(`(?:${OFFSET_KEYS.join("|")})[ \\t]*:[ \\t]*-?\\d+`);

/**
 * True when `input` begins with a CF_HTML descriptor block.
 *
 * Requires the `Version:` line AND at least one numeric offset key, so ordinary prose that happens to
 * open with the word "Version:" — a changelog, a release note — is not treated as clipboard plumbing.
 */
export function hasClipboardHtmlDescriptor(input: string | null | undefined): boolean {
    if (!input) {
        return false;
    }
    const match = DESCRIPTOR_RE.exec(input);
    return match !== null && OFFSET_KEY_RE.test(match[0]);
}

/**
 * Returns `input` with a leading CF_HTML descriptor block removed, or `input` unchanged when there is
 * none. Never throws, and returns the SAME string reference when nothing was removed so callers can
 * cheaply detect a no-op.
 */
export function stripClipboardHtmlDescriptor(input: string): string;
export function stripClipboardHtmlDescriptor(input: null | undefined): string;
export function stripClipboardHtmlDescriptor(input: string | null | undefined): string {
    if (!input) {
        return input ?? "";
    }
    const match = DESCRIPTOR_RE.exec(input);
    if (match === null || !OFFSET_KEY_RE.test(match[0])) {
        return input;
    }
    return input.slice(match[0].length);
}
